import { z, type ZodType } from '../../core/schema'
import { Column } from '../../core/column'
import { decodeJsonText, encodeJsonb } from '../../utils/codecs'
import { binarySchema, encodeBinary } from '../../utils/binary'
import { ulid, ulidToBytes, bytesToUlid } from '@w/utils'
import type { InsertionType, SqlFragment } from '../../core/types'

type CustomOptions<T, Encoded = T> = {
  encode?: (value: T) => Encoded
  decode?: (value: unknown) => T
  valueSql?: (value: Encoded) => SqlFragment
}

type EnumMap = Record<string, number>
type EnumKey<T extends EnumMap> = Extract<keyof T, string>

// SQLite drivers return numbers, but these columns also run against postgres
// drivers (see Database.setDriver dialect adoption), which return BIGINT as
// bigint or a numeric string depending on the driver.
function decodeNumber(value: unknown): number {
  return typeof value === 'number' ? value : Number(value)
}

/**
 * SQLite column factory functions.
 *
 * SQLite has a dynamic type system with type affinity. These factories
 * map to SQLite's storage classes:
 * - TEXT: for strings
 * - INTEGER: for whole numbers (including boolean as 0/1)
 * - REAL: for floating point numbers
 * - BLOB: for binary data
 *
 * JSON is stored as TEXT and parsed on read.
 */
export const sqliteColumns = {
  custom: <T, Encoded = T>(
    ddl: string,
    schema: ZodType<T>,
    options?: CustomOptions<T, Encoded>,
  ) =>
    new Column<T>(
      ddl,
      schema,
      'optional',
      true,
      options?.encode,
      options?.decode,
      undefined,
      undefined,
      options?.valueSql as ((value: unknown) => SqlFragment) | undefined,
    ),

  generated: <T, I extends InsertionType>(base: Column<T, I>) =>
    base._markGenerated(),

  /**
   * TEXT column (unlimited length string)
   * TypeScript type: string | null
   *
   * Use for UUIDs, strings, and any text data.
   */
  text: () => new Column<string>('TEXT', z.string(), 'optional', true),

  /**
   * INTEGER column
   * TypeScript type: number | null
   *
   * SQLite integers are 64-bit signed, but JS numbers are safe up to 2^53-1.
   * For very large integers, consider storing as TEXT.
   */
  integer: () =>
    new Column<number>(
      'INTEGER',
      z.number().int(),
      'optional',
      true,
      undefined,
      decodeNumber,
    ),

  /**
   * REAL column (floating point)
   * TypeScript type: number | null
   */
  real: () =>
    new Column<number>(
      'REAL',
      z.number(),
      'optional',
      true,
      undefined,
      decodeNumber,
    ),

  /**
   * BLOB column (binary data)
   * TypeScript type: Uint8Array | null
   */
  blob: () =>
    new Column<Uint8Array>(
      'BLOB',
      binarySchema,
      'optional',
      true,
      encodeBinary,
    ),

  /**
   * CRDT column (Turso merges binary frames on assignment).
   * TypeScript type: Uint8Array | null
   */
  crdt: () =>
    new Column<Uint8Array>(
      'CRDT',
      binarySchema,
      'optional',
      true,
      encodeBinary,
    ),

  /**
   * JSON column (stored as TEXT)
   * TypeScript type: T | null (where T is inferred from Zod schema)
   *
   * Values are JSON.stringify'd on write and JSON.parse'd on read.
   * Null bytes are escaped/unescaped automatically.
   *
   * @template T - The TypeScript type of the JSON data
   * @param schema - Zod schema for validation
   */
  json: <T>(schema: ZodType<T>) =>
    new Column<T>(
      'TEXT', // SQLite stores JSON as TEXT
      schema,
      'optional',
      true,
      encodeJsonb,
      (value) => decodeJsonText(value) as T,
    ),

  /**
   * BOOLEAN column (stored as INTEGER 0/1)
   * TypeScript type: boolean | null
   *
   * SQLite doesn't have a native boolean type; uses INTEGER with 0 = false, 1 = true.
   */
  boolean: () =>
    new Column<boolean>(
      'INTEGER',
      z.boolean(),
      'optional',
      true,
      // Encode: boolean -> 0/1
      (value: boolean) => (value ? 1 : 0),
      // Decode: 0/1 -> boolean (via Number: pg drivers may return bigint or
      // string, and Boolean('0') would be true)
      (value: unknown) => decodeNumber(value) !== 0,
    ),

  /**
   * DATETIME column (stored as INTEGER unix milliseconds)
   * TypeScript type: Date | null
   *
   * Dates are stored as unix ms integers and converted to/from Date objects.
   */
  datetime: () =>
    new Column<Date>(
      'INTEGER',
      z.date(),
      'optional',
      true,
      (value: Date) =>
        typeof value === 'string' ? new Date(value).getTime() : value.getTime(),
      // Via Number: new Date('1700000000000') is Invalid Date
      (value: unknown) => new Date(decodeNumber(value)),
    ),

  /**
   * NUMERIC column for arbitrary precision numbers
   * TypeScript type: string | null (to preserve precision)
   *
   * Stored as TEXT to preserve exact decimal representation.
   */
  numeric: () => new Column<string>('TEXT', z.string(), 'optional', true),

  /**
   * ULID column (stored as BLOB binary representation)
   * TypeScript type: string | null
   *
   * Encodes the ULID string into its compact binary form via `ulidToBytes`
   * and decodes back via `bytesToUlid` on read. Smaller and faster to compare
   * than storing the 19-char string.
   *
   * Auto-applies `$defaultFn(ulid)` so userland can omit the column on insert.
   * Override by calling `.$defaultFn(...)` again with a different generator.
   */
  ulid: () =>
    new Column<string>(
      'BLOB',
      z.string(),
      'optional',
      true,
      (value: string) => ulidToBytes(value),
      (value: unknown) => {
        if (typeof value === 'string') return value
        if (!(value instanceof Uint8Array)) {
          throw new TypeError(
            `ULID column expects Uint8Array, got ${typeof value}: ${String(value)}`,
          )
        }
        return bytesToUlid(value)
      },
    ).$defaultFn(() => ulid()),

  /**
   * ENUM column (stored as REAL stable sort code)
   * TypeScript type: key | null (where key is the union of object keys)
   *
   * Values are stored as their mapped numeric code and decoded back to the key on read.
   * Use gaps or decimals (e.g. `{ todo: 10, doing: 20, done: 30 }`) to insert
   * values between existing codes without rewriting stored rows.
   *
   * When deleting an enum value, write a manual migration that deletes rows using
   * the old code (e.g. `DELETE FROM tasks WHERE status = 20`). Do not add enum
   * assertion tables or generated sanity checks. Do not reuse a deleted code
   * unless intentionally changing what old stored data means.
   */
  enum: <const T extends EnumMap>(values: T) => {
    const entries = Object.entries(values) as [EnumKey<T>, number][]
    if (entries.length === 0) {
      throw new Error('SQLite enum requires at least one value')
    }

    const names = entries.map(([name]) => name) as [EnumKey<T>, ...EnumKey<T>[]]
    const byValue = new Map<number, EnumKey<T>>()
    for (const [name, code] of entries) {
      if (typeof code !== 'number' || !Number.isFinite(code)) {
        throw new TypeError(
          `SQLite enum value for "${name}" must be a finite number, got ${String(code)}`,
        )
      }
      const existing = byValue.get(code)
      if (existing) {
        throw new Error(
          `SQLite enum code ${code} is used by both "${existing}" and "${name}"`,
        )
      }
      byValue.set(code, name)
    }

    return new Column<EnumKey<T>>(
      'REAL',
      z.enum(names),
      'optional',
      true,
      (value: EnumKey<T>) => values[value],
      (value: unknown) => {
        const code =
          typeof value === 'number'
            ? value
            : typeof value === 'bigint' || typeof value === 'string'
              ? Number(value)
              : undefined
        if (code === undefined || Number.isNaN(code)) {
          throw new TypeError(
            `Enum column expects number (code), got ${typeof value}: ${String(value)}`,
          )
        }
        const decoded = byValue.get(code)
        if (!decoded) {
          throw new Error(
            `Enum code ${code} is not mapped for values: [${entries
              .map(([name, code]) => `${name}: ${code}`)
              .join(', ')}]`,
          )
        }
        return decoded
      },
    )
  },
}

/** Type of the sqlite column factory */
export type SqliteColumnFactory = typeof sqliteColumns
